import React, { useState, useMemo } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  RadioGroup,
  Radio,
  Stack,
  HStack,
  VStack,
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react'
import {
  calculateAutoLabelsPreview,
  applyAutoLabelsToPlan,
} from './planUtils'

export function AutoLabelModal({
  isOpen,
  onClose,
  plan,
  activeZoneIndex = 0,
  selectedRowIndices = [],
  onApply,
}) {
  const currentZone = plan?.zones?.[activeZoneIndex]

  // Scope: 'selected' or 'all'
  const hasSelection = selectedRowIndices.length > 0
  const [scope, setScope] = useState(hasSelection ? 'selected' : 'all')

  // When opening or selection changes, sync scope
  React.useEffect(() => {
    if (isOpen) {
      setScope(selectedRowIndices.length > 0 ? 'selected' : 'all')
    }
  }, [isOpen, selectedRowIndices.length])

  // Form states
  const [rowLabelType, setRowLabelType] = useState('alphabetical') // 'alphabetical', 'numeric', 'none'
  const [startRowLetter, setStartRowLetter] = useState('A')
  const [startRowNumber, setStartRowNumber] = useState(1)

  const [seatNumberMode, setSeatNumberMode] = useState('continuous') // 'continuous', 'range', 'restart', 'none'
  const [startSeatNumber, setStartSeatNumber] = useState(1)
  const [rangeEndNumber, setRangeEndNumber] = useState(50)
  const [seatDirection, setSeatDirection] = useState('ltr') // 'ltr', 'rtl'

  // Colors
  const headerBg = useColorModeValue('gray.50', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200')
  const inputBg = useColorModeValue('white', 'gray.800')
  const previewBg = useColorModeValue('gray.50', 'gray.800')

  // Target rows based on scope (filter out empty ghost rows with 0 seats)
  const allRowsWithSeats = useMemo(() => {
    if (!currentZone?.rows) return []
    return currentZone.rows
      .map((row, rowIndex) => ({ row, rowIndex }))
      .filter((item) => (item.row?.seats || []).length > 0)
  }, [currentZone?.rows])

  const emptyRowCount = (currentZone?.rows?.length || 0) - allRowsWithSeats.length

  const targetRowsWithMetadata = useMemo(() => {
    if (scope === 'selected' && hasSelection) {
      const selectedSet = new Set(selectedRowIndices)
      return allRowsWithSeats.filter((item) => selectedSet.has(item.rowIndex))
    }
    return allRowsWithSeats
  }, [allRowsWithSeats, scope, hasSelection, selectedRowIndices])

  // Total affected seats
  const totalSeats = useMemo(() => {
    return targetRowsWithMetadata.reduce(
      (sum, item) => sum + (item.row?.seats?.length || 0),
      0
    )
  }, [targetRowsWithMetadata])

  // Check if existing rows/seats already have labels/numbers
  const hasExistingLabels = useMemo(() => {
    return targetRowsWithMetadata.some(
      (item) =>
        (item.row.row_label && item.row.row_label !== '') ||
        (item.row.seats || []).some((s) => s.seat_number && s.seat_number !== '')
    )
  }, [targetRowsWithMetadata])

  // Computed preview
  const startRowLabel =
    rowLabelType === 'alphabetical'
      ? startRowLetter || 'A'
      : String(startRowNumber || 1)

  const previewData = useMemo(() => {
    if (targetRowsWithMetadata.length === 0) return []
    return calculateAutoLabelsPreview({
      rowsWithMetadata: targetRowsWithMetadata,
      rowLabelType,
      startRowLabel,
      seatNumberMode,
      startSeatNumber: Number(startSeatNumber) || 1,
      endSeatNumber: Number(rangeEndNumber) || 50,
      seatDirection,
    })
  }, [
    targetRowsWithMetadata,
    rowLabelType,
    startRowLabel,
    seatNumberMode,
    startSeatNumber,
    rangeEndNumber,
    seatDirection,
  ])

  const handleApply = () => {
    if (targetRowsWithMetadata.length === 0) {
      onClose()
      return
    }

    const updatedPlan = applyAutoLabelsToPlan({
      plan,
      activeZoneIndex,
      rowsWithMetadata: targetRowsWithMetadata,
      rowLabelType,
      startRowLabel,
      seatNumberMode,
      startSeatNumber: Number(startSeatNumber) || 1,
      endSeatNumber: Number(rangeEndNumber) || 50,
      seatDirection,
    })

    if (onApply) {
      onApply(updatedPlan, {
        rowCount: targetRowsWithMetadata.length,
        seatCount: totalSeats,
      })
    }
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="700px">
        <ModalHeader pb={1}>
          <HStack spacing={2} align="center">
            <Text>Auto Label Rows & Number Seats</Text>
            <Badge colorScheme="brand" fontSize="xs">
              {targetRowsWithMetadata.length} rows ({totalSeats} seats)
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={4}>
          <VStack spacing={4} align="stretch">
            {/* Scope Selection */}
            {hasSelection && allRowsWithSeats.length > targetRowsWithMetadata.length && (
              <Box p={3} borderRadius="md" border="1px solid" borderColor={borderColor}>
                <FormLabel fontSize="xs" fontWeight="700" mb={2}>
                  Apply To
                </FormLabel>
                <RadioGroup value={scope} onChange={setScope} size="sm">
                  <Stack direction="row" spacing={6}>
                    <Radio value="selected">
                      Selected Rows ({targetRowsWithMetadata.length})
                    </Radio>
                    <Radio value="all">
                      All Rows in Zone ({allRowsWithSeats.length}{emptyRowCount > 0 ? ` · ${emptyRowCount} empty excluded` : ''})
                    </Radio>
                  </Stack>
                </RadioGroup>
                {emptyRowCount > 0 && (
                  <Text fontSize="2xs" color="gray.400" mt={1}>
                    * {emptyRowCount} empty row(s) with 0 seats are excluded to prevent skipping letters.
                  </Text>
                )}
              </Box>
            )}

            {/* Row Labeling Configuration */}
            <Box p={3} borderRadius="md" border="1px solid" borderColor={borderColor}>
              <Text fontSize="xs" fontWeight="700" mb={2} color="brand.500">
                1. Row Labels (Top to Bottom visual order)
              </Text>
              <HStack spacing={6} align="flex-start" flexWrap="wrap">
                <FormControl w="auto">
                  <FormLabel fontSize="xs" color="gray.500" mb={1}>
                    Label Type
                  </FormLabel>
                  <RadioGroup
                    value={rowLabelType}
                    onChange={setRowLabelType}
                    size="sm"
                  >
                    <Stack direction="row" spacing={4}>
                      <Radio value="alphabetical">Alphabetical (A, B, C...)</Radio>
                      <Radio value="numeric">Numeric (1, 2, 3...)</Radio>
                      <Radio value="none">Do Not Change</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                {rowLabelType === 'alphabetical' && (
                  <FormControl maxW="120px">
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>
                      Start Letter
                    </FormLabel>
                    <Input
                      size="sm"
                      bg={inputBg}
                      maxLength={3}
                      value={startRowLetter}
                      textTransform="uppercase"
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, '')
                        setStartRowLetter(val || 'A')
                      }}
                    />
                  </FormControl>
                )}

                {rowLabelType === 'numeric' && (
                  <FormControl maxW="120px">
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>
                      Start Row Number
                    </FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      min={1}
                      bg={inputBg}
                      value={startRowNumber}
                      onChange={(e) =>
                        setStartRowNumber(Math.max(1, parseInt(e.target.value) || 1))
                      }
                    />
                  </FormControl>
                )}
              </HStack>
            </Box>

            {/* Seat Numbering Configuration */}
            <Box p={3} borderRadius="md" border="1px solid" borderColor={borderColor}>
              <Text fontSize="xs" fontWeight="700" mb={2} color="brand.500">
                2. Seat Numbering
              </Text>
              <VStack spacing={3} align="stretch">
                <FormControl w="auto">
                  <FormLabel fontSize="xs" color="gray.500" mb={1}>
                    Numbering Mode
                  </FormLabel>
                  <RadioGroup
                    value={seatNumberMode}
                    onChange={setSeatNumberMode}
                    size="sm"
                  >
                    <Stack direction="row" spacing={4} flexWrap="wrap">
                      <Radio value="continuous">
                        Continuous across rows (1–10, 11–20...)
                      </Radio>
                      <Radio value="range">
                        Continuous within range (e.g. 1–50, 1–50...)
                      </Radio>
                      <Radio value="restart">
                        Restart per row (1–10, 1–10...)
                      </Radio>
                      <Radio value="none">Do Not Change</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                {seatNumberMode === 'range' && (
                  <HStack spacing={3} align="flex-end" flexWrap="wrap">
                    <FormControl maxW="110px">
                      <FormLabel fontSize="xs" color="gray.500" mb={1}>
                        Start Number
                      </FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        min={1}
                        bg={inputBg}
                        value={startSeatNumber}
                        onChange={(e) =>
                          setStartSeatNumber(Math.max(1, parseInt(e.target.value) || 1))
                        }
                      />
                    </FormControl>
                    <FormControl maxW="110px">
                      <FormLabel fontSize="xs" color="gray.500" mb={1}>
                        End Number (Limit)
                      </FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        min={Number(startSeatNumber) || 1}
                        bg={inputBg}
                        value={rangeEndNumber}
                        onChange={(e) =>
                          setRangeEndNumber(Math.max(Number(startSeatNumber) || 1, parseInt(e.target.value) || 1))
                        }
                      />
                    </FormControl>
                    <Text fontSize="xs" color="gray.500" pb={2}>
                      (Cycles {startSeatNumber} to {rangeEndNumber}, then restarts at {startSeatNumber})
                    </Text>
                  </HStack>
                )}

                {(seatNumberMode === 'continuous' || seatNumberMode === 'restart') && (
                  <FormControl maxW="120px">
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>
                      Start Number
                    </FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      min={1}
                      bg={inputBg}
                      value={startSeatNumber}
                      onChange={(e) =>
                        setStartSeatNumber(Math.max(1, parseInt(e.target.value) || 1))
                      }
                    />
                  </FormControl>
                )}

                {seatNumberMode !== 'none' && (
                  <HStack spacing={4} align="center">
                    <Text fontSize="xs" color="gray.500">
                      Spatial Order:
                    </Text>
                    <RadioGroup
                      value={seatDirection}
                      onChange={setSeatDirection}
                      size="xs"
                    >
                      <Stack direction="row" spacing={4}>
                        <Radio value="ltr">Left to Right (1 → 2 → 3)</Radio>
                        <Radio value="rtl">Right to Left (3 → 2 → 1)</Radio>
                      </Stack>
                    </RadioGroup>
                  </HStack>
                )}
              </VStack>
            </Box>

            {/* Warning if existing labels are present */}
            {hasExistingLabels && (
              <Alert status="warning" borderRadius="md" size="sm" py={2}>
                <AlertIcon boxSize="16px" />
                <AlertDescription fontSize="xs">
                  Warning: Applying will overwrite existing row labels and seat numbers
                  on the selected {targetRowsWithMetadata.length} rows ({totalSeats} seats).
                </AlertDescription>
              </Alert>
            )}

            <Divider borderColor={borderColor} />

            {/* Live Preview Table */}
            <Box>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="xs" fontWeight="700">
                  Preview ({previewData.length} rows)
                </Text>
                <Text fontSize="xs" color="gray.400">
                  Ordered Top to Bottom
                </Text>
              </Flex>

              <Box
                maxH="220px"
                overflowY="auto"
                border="1px solid"
                borderColor={borderColor}
                borderRadius="md"
                bg={previewBg}
              >
                <Table size="sm" variant="simple">
                  <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th fontSize="10px" py={1.5}>Order</Th>
                      <Th fontSize="10px" py={1.5}>Row Label</Th>
                      <Th fontSize="10px" py={1.5}>Seat Range</Th>
                      <Th fontSize="10px" py={1.5} isNumeric>Seats</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {previewData.length === 0 ? (
                      <Tr>
                        <Td colSpan={4} textAlign="center" py={4} color="gray.400" fontSize="xs">
                          No rows selected.
                        </Td>
                      </Tr>
                    ) : (
                      previewData.map((item, idx) => (
                        <Tr key={item.rowIndex} _hover={{ bg: headerBg }}>
                          <Td fontSize="xs" color="gray.400" py={1.5}>
                            #{idx + 1}
                          </Td>
                          <Td py={1.5}>
                            <HStack spacing={1.5}>
                              <Badge colorScheme="blue" fontSize="xs" px={2}>
                                {item.newRowLabel}
                              </Badge>
                              {item.oldRowLabel && item.oldRowLabel !== item.newRowLabel && (
                                <Text fontSize="10px" color="gray.400" textDecoration="line-through">
                                  {item.oldRowLabel}
                                </Text>
                              )}
                            </HStack>
                          </Td>
                          <Td fontSize="xs" fontWeight="600" py={1.5}>
                            {item.seatRangeText}
                          </Td>
                          <Td fontSize="xs" isNumeric py={1.5}>
                            {item.seatCount}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            size="sm"
            onClick={handleApply}
            isDisabled={targetRowsWithMetadata.length === 0}
          >
            Apply Auto Labeling
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
